<template>
    <section class="base-card-section">
        <div class="base-card-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />
            <ScrollRow>
                <article v-for="(cell, idx) in props.item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="base-card"
                    @click="handleCellClick(cell)">
                    <MediaCard class="base-card__media" v-if="getCellCover(cell)" :url="getCellCover(cell)"
                        :name="getCellTitle(cell)" video-popup />
                    <div class="base-card__body">
                        <h4 v-if="getCellTitle(cell)" class="base-card__title">{{ getCellTitle(cell) }}</h4>
                        <p v-if="getCellDesc(cell)" class="base-card__desc">{{ getCellDesc(cell) }}</p>
                        <a v-if="hasLearnMore(cell)" class="base-card__link" @click.stop="handleCellClick(cell)">
                            {{ getLinkText(cell) }}<span class="base-card__link-arrow">></span>
                        </a>
                    </div>
                </article>
            </ScrollRow>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import { getStrWithRecord, jumpToUrl } from '.';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

function getCellCover(cell: unknown): string {
    return getStrWithRecord(cell, 'image') || ''
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title') || ''
}
function getCellDesc(cell: unknown): string {
    return getStrWithRecord(cell, 'subTitle') || ''
}
function hasLearnMore(cell: unknown): boolean {
    return getStrWithRecord(cell, 'url') !== ''
}
function getLinkText(cell: unknown): string {
    return getStrWithRecord(cell, 'urlTitle') || ''
}



function handleCellClick(cell: unknown): void {
    jumpToUrl(getStrWithRecord(cell, 'url'))
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$card-radius: 20px;
$body-bg: #f0f1f3;
$link-color: #2563eb;

.base-card-section {
    width: 100%;
}

.base-card-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.base-card-section__header {
    text-align: center;
    margin-bottom: 2rem;

    @media (min-width: $breakpoint-md) {
        margin-bottom: 2.75rem;
    }
}


.base-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: $card-radius;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    height: 100%;
    width: 100%;

    @media (min-width: $breakpoint-md) {
        max-width: 300px;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);

        .base-card__media img {
            transform: scale(1.05);
        }

        .base-card__link-arrow {
            transform: translateX(3px);
        }
    }

    &__media {
        position: relative;
        width: 100%;
        aspect-ratio: 4 / 3;
        overflow: hidden;
        background: $color-bg-tertiary;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
            aspect-ratio: 506 / 392;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        padding: 1.25rem 1.5rem 1.5rem;
        background: $body-bg;
        flex: 1;
    }

    &__title {
        font-size: 1.375rem;
        font-weight: 800;
        color: $color-text;
        margin: 0 0 0.5rem;
        line-height: 1.3;
        letter-spacing: -0.01em;

        @media (min-width: $breakpoint-md) {
            font-size: 1.5rem;
        }
    }

    &__desc {
        font-size: $font-size-sm;
        color: $color-text-secondary;
        margin: 0 0 1rem;
        line-height: 1.6;
        flex: 1;
    }

    &__link {
        display: inline-flex;
        align-items: center;
        gap: 0;
        font-size: $font-size-sm;
        font-weight: 600;
        color: $link-color;
        text-decoration: none;
        margin-top: auto;
        cursor: pointer;
    }

    &__link-arrow {
        font-size: $font-size-sm;
        font-weight: 700;
        margin-left: 2px;
        transition: transform 0.2s ease;
    }
}
</style>
